import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BedDoubleIcon, CalendarPlusIcon, CarIcon, CheckIcon, ClockIcon, InfoIcon, MapPinIcon, PlaneIcon, TriangleAlertIcon, UsersIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { AbsenceModal } from '../../components/member/AbsenceModal';
import { EventDocuments } from '../../components/shared/EventDocuments';
import { missingFields } from '../../utils/personal';
import type { TransportEntry, TransportMode } from '../../types';
import { documentTypeLabel, eventTitle, eventTypeClass, eventTypeLabel, formatDate, formatRange, fullName, functionLabel, personalFieldLabel, transportModeLabel, voiceLabel } from '../../utils/labels';
type Zavihek = 'pregled' | 'prijava' | 'potovanje' | 'nastanitev' | 'prevoz' | 'gradiva';
export function MemberEventDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    events,
    getRsvp,
    setRsvp,
    isExpected,
    currentMember,
    expectedFor,
    pushToast,
    rooms,
    transport,
    assignRoom,
    setTransport,
    members
  } = useApp();
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [zavihek, setZavihek] = useState<Zavihek>('pregled');
  const event = events.find(e => e.id === id);
  const me = currentMember!;
  if (!event) {
    return <Card className="p-10 text-center" data-id="element-1395" data-mp="1q2g6snq9buto">
        <p className="text-sm font-medium text-ink" data-id="element-1396" data-mp="1uvj2b1elheci">Dogodek ne obstaja.</p>
        <Link to="/clan/dogodki" className="mt-2 inline-block text-[13px] font-medium text-green-600" data-id="element-1397" data-mp="ydd0l2et8aee">
          Nazaj na dogodke
        </Link>
      </Card>;
  }
  const rsvp = getRsvp(event.id);
  const pricakovan = isExpected(event, me.id);
  const udelezenci = expectedFor(event);
  const potovanje = event.potovanje?.aktivno ? event.potovanje : undefined;
  const nastanitev = potovanje?.nastanitev?.aktivno ? potovanje.nastanitev : undefined;
  const prevozAktiven = !!potovanje?.prevoz?.aktivno;
  const gradiva = event.dokumenti ?? [];
  const manjka = potovanje ? missingFields(me, potovanje.obveznaPolja) : [];
  const mojaSoba = nastanitev ? rooms[event.id]?.[me.id] : undefined;
  const soseba = nastanitev?.sobe.find(r => r.id === mojaSoba);
  const sostanovalci = udelezenci.filter(m => m.id !== me.id && rooms[event.id]?.[m.id] === mojaSoba);
  const mojPrevoz: TransportEntry | undefined = transport[event.id]?.[me.id];
  const vozniki = udelezenci.map(m => ({
    m,
    t: transport[event.id]?.[m.id]
  })).filter(x => x.t?.nacin === 'vozim');
  const zavihki: {
    key: Zavihek;
    label: string;
    opozorilo?: boolean;
  }[] = useMemo(() => {
    const list: {
      key: Zavihek;
      label: string;
      opozorilo?: boolean;
    }[] = [{
      key: 'pregled',
      label: 'Pregled'
    }];
    if (pricakovan) list.push({
      key: 'prijava',
      label: 'Moja prijava'
    });
    if (potovanje) list.push({
      key: 'potovanje',
      label: 'Potovalni podatki',
      opozorilo: manjka.length > 0
    });
    if (nastanitev) list.push({
      key: 'nastanitev',
      label: 'Nastanitev',
      opozorilo: !mojaSoba
    });
    if (prevozAktiven) list.push({
      key: 'prevoz',
      label: 'Prevoz',
      opozorilo: !mojPrevoz
    });
    if (gradiva.length > 0) list.push({
      key: 'gradiva',
      label: 'Gradiva'
    });
    return list;
  }, [pricakovan, potovanje, nastanitev, prevozAktiven, gradiva.length, manjka.length, mojaSoba, mojPrevoz]);
  function nastaviPrevoz(nacin: TransportMode) {
    if (nacin === 'vozim') {
      setTransport(event!.id, me.id, {
        nacin,
        mesta: mojPrevoz?.mesta ?? 3,
        kraj: mojPrevoz?.kraj ?? 'Brežice',
        ura: mojPrevoz?.ura ?? event!.zacetek,
        opomba: mojPrevoz?.opomba
      });
    } else {
      setTransport(event!.id, me.id, {
        nacin
      });
    }
    pushToast('Prevoz je zabeležen.');
  }
  return <div className="mx-auto max-w-3xl space-y-4" data-id="element-1398" data-mp="2te6qp1bb0cgj">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-1399" data-mp="2ogez68ekmscv">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1400" data-mp="2ekkc913beb4a" />
        Nazaj
      </button>

      <Card className="p-5" data-id="element-1401" data-mp="3c7kduk8rzell">
        <div className="flex flex-wrap items-center gap-1.5" data-id="element-1402" data-mp="3c5puvbh2dwk5">
          <Badge className={eventTypeClass[event.type]} data-id="element-1403" data-mp="377eiou3ar9yr">{eventTypeLabel[event.type]}</Badge>
          {event.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-1404" data-mp="32gg0foyr01sv">{event.opomba}</Badge>}
          {potovanje && <Badge className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-1405" data-mp="2xh3bmyobgknn">
              <PlaneIcon className="h-3 w-3" data-id="element-1406" data-mp="3uxsd2r9ctkwi" />
              Gostovanje
            </Badge>}
          {!event.steje && <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-1407" data-mp="3q591fzu88zuc">Ne šteje v prisotnost</Badge>}
        </div>

        <h1 className="mt-2.5 text-2xl font-semibold text-ink" data-id="element-1408" data-mp="2cesugsfbaz5">{eventTitle(event)}</h1>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-muted" data-id="element-1409" data-mp="6bxa92l1d3x8">
          <span className="flex items-center gap-1.5" data-id="element-1410" data-mp="b9ffoizuh10h">
            <ClockIcon className="h-4 w-4" data-id="element-1411" data-mp="ga3cxmeo2e35" />
            {event.datumDo ? formatRange(event.datum, event.datumDo) : `${formatDate(event.datum, {
            weekday: true,
            long: true
          })} · ${event.zacetek}${event.konec ? `–${event.konec}` : ''}`}
          </span>
          <span className="flex items-center gap-1.5" data-id="element-1412" data-mp="u2j8y395hbuk">
            <MapPinIcon className="h-4 w-4" data-id="element-1413" data-mp="z0b9flnv26ad" />
            {event.lokacija}
          </span>
        </p>

        {(manjka.length > 0 || nastanitev && !mojaSoba || prevozAktiven && !mojPrevoz) && <div className="mt-4 flex flex-wrap gap-2" data-id="element-1414" data-mp="18y8zmepa6d5m">
            {manjka.length > 0 && <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-id="element-1415" data-mp="jad0qe6arrkv">
                <TriangleAlertIcon className="h-3 w-3" data-id="element-1416" data-mp="j88z62vvf4nr" />
                Manjkajo osebni podatki
              </Badge>}
            {nastanitev && !mojaSoba && <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-1417" data-mp="t1prf2gd0bpb">Soba še ni določena</Badge>}
            {prevozAktiven && (mojPrevoz ? <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1418" data-mp="1201qlsvakhtd">
                  Prevoz urejen
                </Badge> : <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-id="element-1419" data-mp="1byqziw57mrxh">Prevoz še ni urejen</Badge>)}
          </div>}
      </Card>

      {zavihki.length > 1 && <div role="tablist" aria-label="Razdelki dogodka" className="flex gap-1.5 overflow-x-auto viva-scroll pb-1" data-id="element-1420" data-mp="3dqdut9w4x8zr">
          {zavihki.map(z => <button key={z.key} role="tab" aria-selected={zavihek === z.key} onClick={() => setZavihek(z.key)} className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${zavihek === z.key ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-1421" data-mp="38tnf4no8sfev">
              {z.label}
              {z.opozorilo && <span className={`h-1.5 w-1.5 rounded-full ${zavihek === z.key ? 'bg-white' : 'bg-amber-500'}`} data-id="element-1422" data-mp="19i12ay0uxacb" />}
            </button>)}
        </div>}

      {zavihek === 'pregled' && <Card className="p-5" data-id="element-1423" data-mp="2pzx5ir5z4bl9">
          <dl className="grid gap-3 sm:grid-cols-2" data-id="element-1424" data-mp="2pwj01a20jm9l">
            <div className="flex items-start gap-2.5" data-id="element-1425" data-mp="2uwnjn8y7wx41">
              <UsersIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" data-id="element-1426" data-mp="2uvcfv94rvpcx" />
              <div data-id="element-1427" data-mp="1hq2cfr3ks1yo">
                <dt className="text-[13px] text-ink-muted" data-id="element-1428" data-mp="1hsooo41ev5n8">Za koga velja</dt>
                <dd className="text-sm font-medium text-ink" data-id="element-1429" data-mp="1dq1z69s7pwt7">
                  {event.obcinstvo === 'glasovi' && event.glasovi ? event.glasovi.map(v => voiceLabel[v]).join(', ') : event.obcinstvo === 'funkcije' && event.funkcije ? event.funkcije.map(f => functionLabel[f]).join(', ') : event.obcinstvo === 'aktivni' ? 'Vsi aktivni člani' : event.obcinstvo === 'izbrani' ? 'Izbrani člani' : 'Vsi člani in gostje'}{' '}
                  <span className="font-normal text-ink-muted" data-id="element-1430" data-mp="l2o28boil193">({udelezenci.length} pričakovanih)</span>
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-2.5" data-id="element-1431" data-mp="2ffnmwj7r4ln8">
              <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" data-id="element-1432" data-mp="2my50b0ir0klh" />
              <div data-id="element-1433" data-mp="2mw0xyn0hztnb">
                <dt className="text-[13px] text-ink-muted" data-id="element-1434" data-mp="2rx864n6jyuui">Prisotnost</dt>
                <dd className="text-sm font-medium text-ink" data-id="element-1435" data-mp="2rvdg4ugfccjk">
                  {event.steje ? 'Šteje v statistiko prisotnosti' : 'Ne šteje v statistiko'}
                </dd>
              </div>
            </div>
          </dl>

          {event.opis && <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-sm leading-relaxed text-ink" data-id="element-1436" data-mp="2402hj3gsnjw4">
              {event.opis}
            </p>}

          <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-line pt-3" data-id="element-1437" data-mp="290ggzx4bxlij">
            <button onClick={() => pushToast('Dogodek je pripravljen za dodajanje v koledar.')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-green-600 hover:underline" data-id="element-1438" data-mp="2dzjk3c0r1hnv">
              <CalendarPlusIcon className="h-4 w-4" data-id="element-1439" data-mp="3tdo1gsceyrc4" />
              Dodaj v koledar
            </button>
            <Link to="/clan/odsotnost" className="text-[13px] font-medium text-ink-muted hover:text-ink" data-id="element-1440" data-mp="2z2urv6orklme">
              Prijavi daljšo odsotnost
            </Link>
          </div>
        </Card>}

      {zavihek === 'prijava' && <Card className="p-5" data-id="element-1441" data-mp="38smrifbq04xq">
          <h2 className="text-base font-semibold text-ink" data-id="element-1442" data-mp="38uh518knwurs">Vaša udeležba</h2>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1443" data-mp="3dqfuz0ln2kub">
            {rsvp?.status === 'pridem' ? 'Potrdili ste udeležbo.' : rsvp?.status === 'ne_morem' ? `Sporočili ste odsotnost${rsvp.razlog ? ` — ${rsvp.razlog.toLowerCase()}` : ''}.` : 'Prosimo, da sporočite, ali boste prisotni.'}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-1444" data-mp="2v0zs46mhegaq">
            <Button variant={rsvp?.status === 'pridem' ? 'primary' : 'secondary'} onClick={() => {
          setRsvp(event.id, {
            status: 'pridem'
          });
          pushToast('Udeležba je potrjena.');
        }} data-id="element-1445" data-mp="2uydfhskqhaln">
              <CheckIcon className="h-4 w-4" data-id="element-1446" data-mp="1us07cc3q9tt9" />
              Pridem
            </Button>
            <Button variant={rsvp?.status === 'ne_morem' ? 'danger' : 'secondary'} onClick={() => setAbsenceOpen(true)} data-id="element-1447" data-mp="1purixtrzktjk">
              <XIcon className="h-4 w-4" data-id="element-1448" data-mp="1g3x0l0hv2e3e" />
              Ne morem priti
            </Button>
          </div>

          {potovanje && <div className="mt-5 border-t border-line pt-4" data-id="element-1449" data-mp="1b5bvonrnz68m">
              <h3 className="text-sm font-semibold text-ink" data-id="element-1450" data-mp="17377w5nwd3tw">Osebni podatki za gostovanje</h3>
              {manjka.length === 0 ? <p className="mt-1.5 flex items-center gap-2 text-[13px] text-green-700" data-id="element-1451" data-mp="124njlyltzfyd">
                  <CheckIcon className="h-4 w-4" data-id="element-1452" data-mp="x5t0swnv5re9" />
                  Vaši podatki so popolni.
                </p> : <>
                  <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-1453" data-mp="2rism96exdftg">
                    Manjka: {manjka.map(p => personalFieldLabel[p].toLowerCase()).join(', ')}.
                  </p>
                  <Link to="/clan/profil" data-id="element-1454" data-mp="prs4yy9hqfnj">
                    <Button size="sm" className="mt-3" data-id="element-1455" data-mp="ukl8jit83hy1">
                      Dopolni osebne podatke
                    </Button>
                  </Link>
                </>}
            </div>}
        </Card>}

      {zavihek === 'potovanje' && potovanje && <Card className="p-5" data-id="element-1456" data-mp="18jkzwy84eaxw">
          <h2 className="text-base font-semibold text-ink" data-id="element-1457" data-mp="18lfjo97nnx0e">Potovalni podatki</h2>
          <dl className="mt-3 divide-y divide-line border-t border-line" data-id="element-1458" data-mp="1djh0enhlzk8p">
            {[['Trajanje', formatRange(event.datum, event.datumDo)], ['Odhod', potovanje.odhod ?? '—'], ['Vrnitev', potovanje.prihod ?? '—'], ['Zborno mesto', potovanje.zborno ?? '—'], ['Nastanitev', nastanitev?.ime ?? '—']].map(([k, v]) => <div key={k} className="flex flex-wrap gap-2 py-2.5" data-id="element-1459" data-mp="3ocbhijtv59o6">
                <dt className="w-40 shrink-0 text-[13px] text-ink-muted" data-id="element-1460" data-mp="26ddlghhpxhz9">{k}</dt>
                <dd className="text-sm text-ink" data-id="element-1461" data-mp="26fgkts4rgngn">{v}</dd>
              </div>)}
          </dl>

          {potovanje.opis && <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-sm leading-relaxed text-ink" data-id="element-1462" data-mp="2feli6v4l6ar8">
              {potovanje.opis}
            </p>}

          <div className="mt-5 border-t border-line pt-4" data-id="element-1463" data-mp="2bavvkb7h238u">
            <h3 className="text-sm font-semibold text-ink" data-id="element-1464" data-mp="1nnxjdpqdxhgq">Za to gostovanje potrebujemo</h3>
            <ul className="mt-2 space-y-1.5" data-id="element-1465" data-mp="1ip42dnbw38bt">
              {potovanje.obveznaPolja.map(p => {
            const jeVpisano = !missingFields(me, [p]).length;
            const vrednost = p === 'dokumentTip' && me.dokumentTip ? documentTypeLabel[me.dokumentTip] : p === 'datumRojstva' && me.datumRojstva ? formatDate(me.datumRojstva) : p === 'dokumentStevilka' ? 'vpisano' : me[p] as string | undefined;
            return <li key={p} className="flex items-center gap-2 text-sm" data-id="element-1466" data-mp="1hmx7hlo0qj5z">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${jeVpisano ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-1467" data-mp="1ml8g5t2kunmm">
                      {jeVpisano ? <CheckIcon className="h-3 w-3" data-id="element-1468" data-mp="20cd9yej9klp1" /> : <XIcon className="h-3 w-3" data-id="element-1469" data-mp="20bllrorzxq8s" />}
                    </span>
                    <span className="text-ink" data-id="element-1470" data-mp="25bpwsza4xsrm">{personalFieldLabel[p]}</span>
                    <span className="text-[13px] text-ink-muted" data-id="element-1471" data-mp="259n8cjoms7p0">
                      {jeVpisano ? `· ${vrednost}` : '· ni vpisano'}
                    </span>
                  </li>;
          })}
            </ul>
            <Link to="/clan/profil" data-id="element-1472" data-mp="fw0a4vscrscx">
              <Button size="sm" variant="secondary" className="mt-3" data-id="element-1473" data-mp="fx1sllbp7h4o">
                Odpri moj profil
              </Button>
            </Link>
          </div>
        </Card>}

      {zavihek === 'nastanitev' && nastanitev && <div className="space-y-4" data-id="element-1474" data-mp="3uslfep325ygp">
          <Card className="p-5" data-id="element-1475" data-mp="3utn2xt19gdfb">
            <div className="flex items-center gap-2" data-id="element-1476" data-mp="3usv50bk6y3lv">
              <BedDoubleIcon className="h-4 w-4 text-green-600" data-id="element-1477" data-mp="3pua4eb111hww" />
              <h2 className="text-base font-semibold text-ink" data-id="element-1478" data-mp="3pro14nl65okh">Moja soba</h2>
            </div>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1479" data-mp="r9iq9tpzf50d">{nastanitev.ime}</p>

            {soseba ? <div className="mt-4 rounded-lg border border-green-200 bg-green-50/60 p-4" data-id="element-1480" data-mp="hdp3lhijtv4v">
                <p className="text-sm font-semibold text-ink" data-id="element-1481" data-mp="hfibt18t8nez">{soseba.ime}</p>
                <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1482" data-mp="cecip2b8my4x">
                  Kapaciteta {soseba.kapaciteta} · {sostanovalci.length + 1} zasedenih
                </p>
                <div className="mt-3" data-id="element-1483" data-mp="2x3s1pb47tik0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1484" data-mp="2x6xv32jvn6lj">
                    Sostanovalci
                  </p>
                  <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1485" data-mp="1t5y5zkjq4923">
                    {sostanovalci.length === 0 ? <li className="text-[13px] text-ink-muted" data-id="element-1486" data-mp="1f6opskvipey4">Zaenkrat ste v sobi sami.</li> : sostanovalci.map(m => <li key={m.id} data-id="element-1487" data-mp="15h55das5pd15">
                          <Badge className="bg-white text-ink border-line" data-id="element-1488" data-mp="15g3vvaqsgylj">{fullName(m)}</Badge>
                        </li>)}
                  </ul>
                </div>
              </div> : <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-[13px] text-ink-muted" data-id="element-1489" data-mp="11fa8cih9dlt6">
                Soba še ni določena.{' '}
                {nastanitev.nacin === 'vodstvo' ? 'Razporeditev pripravlja vodstvo zbora.' : 'Izberite prosto sobo spodaj.'}
              </p>}
          </Card>

          {nastanitev.nacin === 'clani' && <Card className="p-5" data-id="element-1490" data-mp="3uiernflqli4">
              <h2 className="text-base font-semibold text-ink" data-id="element-1491" data-mp="3vk14o22iuf5">Proste sobe</h2>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1492" data-mp="hu9y83ow0u3i">
                Izberete lahko sobo, dokler ni zasedena. Končno razporeditev potrdi vodstvo.
              </p>
              <ul className="mt-3 space-y-2" data-id="element-1493" data-mp="quom9at8knap">
                {nastanitev.sobe.map(r => {
            const zasedeno = udelezenci.filter(m => rooms[event.id]?.[m.id] === r.id);
            const polna = zasedeno.length >= r.kapaciteta;
            const mojaTa = mojaSoba === r.id;
            return <li key={r.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2.5" data-id="element-1494" data-mp="vmg3guqi5go6">
                      <span className="min-w-0 flex-1" data-id="element-1495" data-mp="15kdq5dspr5eo">
                        <span className="block text-sm font-medium text-ink" data-id="element-1496" data-mp="15hhvpzg2qyuz">{r.ime}</span>
                        <span className="block text-[13px] text-ink-muted" data-id="element-1497" data-mp="3meqrb1c5il9r">
                          {zasedeno.length}/{r.kapaciteta} zasedenih ·{' '}
                          {zasedeno.map(m => m.ime).join(', ') || 'prazna'}
                        </span>
                      </span>
                      <Button size="sm" variant={mojaTa ? 'primary' : 'secondary'} disabled={polna && !mojaTa} onClick={() => {
                assignRoom(event.id, me.id, mojaTa ? null : r.id);
                pushToast(mojaTa ? 'Izbira sobe je preklicana.' : `Izbrali ste ${r.ime}.`);
              }} data-id="element-1498" data-mp="53rd6aspdy19">
                        {mojaTa ? 'Moja izbira' : polna ? 'Zasedeno' : 'Izberi'}
                      </Button>
                    </li>;
          })}
              </ul>
            </Card>}
        </div>}

      {zavihek === 'prevoz' && prevozAktiven && <div className="space-y-4" data-id="element-1499" data-mp="12ybkkd63jofp">
          <Card className="p-5" data-id="element-1500" data-mp="12zd4z8905na3">
            <div className="flex items-center gap-2" data-id="element-1501" data-mp="12vxsmd6pqscd">
              <CarIcon className="h-4 w-4 text-green-600" data-id="element-1502" data-mp="xxw7zntn2kys" />
              <h2 className="text-base font-semibold text-ink" data-id="element-1503" data-mp="szunqzkh8flq">Moj prevoz</h2>
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-3" data-id="element-1504" data-mp="o0rh5aoum1bn">
              {(['vozim', 'potrebujem', 'sam'] as TransportMode[]).map(n => <Button key={n} variant={mojPrevoz?.nacin === n ? 'primary' : 'secondary'} onClick={() => nastaviPrevoz(n)} data-id="element-1505" data-mp="j8zzjpno75xn">
                  {transportModeLabel[n]}
                </Button>)}
            </div>

            {mojPrevoz?.nacin === 'vozim' && <div className="mt-4 grid gap-3 border-t border-line pt-4 sm:grid-cols-3" data-id="element-1506" data-mp="5c4cuuehxfz4">
                <Field label="Prosta mesta" htmlFor="mesta" data-id="element-1507" data-mp="d9ws07wy5ip">
                  <input id="mesta" type="number" min={0} value={mojPrevoz.mesta ?? 0} onChange={e => setTransport(event.id, me.id, {
              ...mojPrevoz,
              mesta: Number(e.target.value)
            })} className={inputClass} data-id="element-1508" data-mp="3v76o4ae0yuvd" />
                </Field>
                <Field label="Kraj odhoda" htmlFor="kraj" data-id="element-1509" data-mp="3cayj97oxcm0l">
                  <input id="kraj" value={mojPrevoz.kraj ?? ''} onChange={e => setTransport(event.id, me.id, {
              ...mojPrevoz,
              kraj: e.target.value
            })} className={inputClass} data-id="element-1510" data-mp="37i5ewkxdbfp1" />
                </Field>
                <Field label="Ura odhoda" htmlFor="ura" data-id="element-1511" data-mp="2tje3j69w484l">
                  <input id="ura" type="time" value={mojPrevoz.ura ?? ''} onChange={e => setTransport(event.id, me.id, {
              ...mojPrevoz,
              ura: e.target.value
            })} className={inputClass} data-id="element-1512" data-mp="2tkyxatodrn1e" />
                </Field>
                <Field label="Opomba (neobvezno)" htmlFor="opombaPrevoz" className="sm:col-span-3" data-id="element-1513" data-mp="1d2farlf3f7c5">
                  <input id="opombaPrevoz" value={mojPrevoz.opomba ?? ''} onChange={e => setTransport(event.id, me.id, {
              ...mojPrevoz,
              opomba: e.target.value
            })} placeholder="Npr. prostor za dva kovčka" className={inputClass} data-id="element-1514" data-mp="1i19ucpkvwzxb" />
                </Field>
              </div>}

            {mojPrevoz?.nacin === 'potrebujem' && <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-[13px] text-ink-muted" data-id="element-1515" data-mp="1njn59ygxvjg">
                {mojPrevoz.voznikId ? `Prevoz je urejen — pelje vas ${members.find(m => m.id === mojPrevoz.voznikId)?.ime ?? ''} ${members.find(m => m.id === mojPrevoz.voznikId)?.priimek ?? ''}.` : 'Vodstvo vas bo dodelilo enemu od voznikov.'}
              </p>}
          </Card>

          <Card className="p-5" data-id="element-1516" data-mp="1irorl580h0z0">
            <h2 className="text-base font-semibold text-ink" data-id="element-1517" data-mp="1eo8uy8vxz0mb">Vozniki</h2>
            <ul className="mt-3 divide-y divide-line" data-id="element-1518" data-mp="1eqd0ev9ho7op">
              {vozniki.length === 0 && <li className="py-4 text-[13px] text-ink-muted" data-id="element-1519" data-mp="r0983k1r3ud0">Zaenkrat ni prijavljenih voznikov.</li>}
              {vozniki.map(({
            m,
            t
          }) => {
            const sopotniki = udelezenci.filter(x => transport[event.id]?.[x.id]?.voznikId === m.id);
            const prosto = Math.max((t?.mesta ?? 0) - sopotniki.length, 0);
            return <li key={m.id} className="flex flex-wrap items-center gap-3 py-3" data-id="element-1520" data-mp="26f6off64up8u">
                    <span className="min-w-0 flex-1" data-id="element-1521" data-mp="26hj39jglgjho">
                      <span className="block text-sm font-medium text-ink" data-id="element-1522" data-mp="2ev4drr0cwy65">{fullName(m)}</span>
                      <span className="block text-[13px] text-ink-muted" data-id="element-1523" data-mp="2iytujuasibgm">
                        {t?.kraj}
                        {t?.ura ? ` · odhod ${t.ura}` : ''}
                        {sopotniki.length > 0 ? ` · ${sopotniki.map(s => s.ime).join(', ')}` : ''}
                      </span>
                    </span>
                    <Badge className={prosto > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-ink-muted border-line'} data-id="element-1524" data-mp="2xngmqwlimxp9">
                      {prosto > 0 ? `${prosto} prostih mest` : 'zasedeno'}
                    </Badge>
                  </li>;
          })}
            </ul>
          </Card>
        </div>}

      {zavihek === 'gradiva' && <EventDocuments event={event} data-id="element-1525" data-mp="3le2jvstzexkh" />}

      <AbsenceModal event={event} open={absenceOpen} onClose={() => setAbsenceOpen(false)} data-id="element-1526" data-mp="1wqopibe4o8e6" />
    </div>;
}